import clsx from 'clsx';
import React from 'react';
import Input from '../Input';
import { useButtonSizeSmall } from '../../utils/buttonSizes';
import UserIcon from '../../assets/icons/user-icon.svg?react';
import Button from '../Button';
import Select from '../Select';
import { CompanySize, IndustryType } from '@EHSNavigator/backend';

interface EditOrganizationDetailsModalProps {
  otherClasses?: string;
  formHandler: any;
  handleClose: () => void;
}

const EditOrganizationDetailsModal: React.FC<
  EditOrganizationDetailsModalProps
> = ({ otherClasses, formHandler, handleClose }) => {
  const editOrganizationDetailsModalClasses = clsx(otherClasses);
  const sm = useButtonSizeSmall();

  return (
    <div
      className={editOrganizationDetailsModalClasses}
      data-testid="edit-organization-details-modal"
    >
      <div className="mb-8 flex w-full flex-col items-center gap-2.5 text-center lg:mb-12">
        <UserIcon className="[&>path]:fill-brand-byzantine/100 h-12 w-12 md:h-85 md:w-85" />
        <h4 className="text-brand-byzantine">Edit Company Details</h4>
        <p className="text-brand-byzantine font-inter-variable text-lg leading-5 font-normal">
          Modify your company's details and save changes using the 'Update
          Details' button.
        </p>
      </div>
      <form action="#" onSubmit={formHandler?.handleSubmit}>
        <div className="gird-cols-1 mb-6 grid gap-6 sm:grid-cols-2">
          <Input
            onBlur={formHandler?.handleBlur}
            label="Organization Name*"
            errorMessage={
              formHandler?.touched.companyName &&
              formHandler?.errors.companyName
                ? formHandler?.errors.companyName
                : ''
            }
            disabled={formHandler?.isSubmitting}
            value={formHandler?.values.companyName}
            type="text"
            placeholder="Enter your name"
            onChange={formHandler?.handleChange}
            name="companyName"
            otherClasses={{ container: 'w-full' }}
          />
          <Input
            onBlur={formHandler.handleBlur}
            label="Organization Address*"
            errorMessage={
              formHandler?.touched.organizationAddress &&
              formHandler?.errors.organizationAddress
                ? formHandler?.errors.organizationAddress
                : ''
            }
            disabled={formHandler.isSubmitting}
            value={formHandler.values.organizationAddress}
            type="text"
            placeholder="e.g. California"
            onChange={formHandler.handleChange}
            name="organizationAddress"
            otherClasses={{ container: 'w-full' }}
            aria-required={true}
            aria-invalid={
              formHandler?.touched.organizationAddress &&
              formHandler?.errors.organizationAddress
                ? true
                : false
            }
          />
        </div>
        <div className="gird-cols-1 mb-6 grid gap-6 sm:grid-cols-2">
          <Select
            label="Industry Type*"
            list={Object.entries(IndustryType).map(([, value]) => ({
              label: value,
              value: value,
            }))}
            value={formHandler.values.industryType}
            disabled={formHandler.isSubmitting}
            placeholder="Industry Types"
            otherClasses={{
              container: 'w-full',
              listContainer: 'bottom-18 top-auto',
              button:
                '[&>span]:wrap-anywhere [&>span]:line-clamp-1 [&>span]:w-full [&>span]:whitespace-normal',
            }}
            onSelect={(e) =>
              formHandler?.setFieldValue('industryType', e, true)
            }
            errorMessage={
              formHandler?.touched.industryType &&
              formHandler?.errors.industryType
                ? formHandler?.errors.industryType
                : ''
            }
            aria-required={true}
            aria-invalid={
              formHandler?.touched.industryType &&
              formHandler?.errors.industryType
                ? true
                : false
            }
          />
          <Select
            label="Industry Size*"
            list={Object.entries(CompanySize).map(([, value]) => ({
              label: value,
              value: value,
            }))}
            value={formHandler.values.industrySize}
            disabled={formHandler.isSubmitting}
            placeholder="e.g. 301+ employees"
            otherClasses={{
              container: 'w-full',
              listContainer: 'bottom-18 top-auto',
            }}
            onSelect={(e) =>
              formHandler?.setFieldValue('industrySize', e, true)
            }
            errorMessage={
              formHandler?.touched.industrySize &&
              formHandler?.errors.industrySize
                ? formHandler?.errors.industrySize
                : ''
            }
          />
        </div>

        <div className="gird-cols-1 mb-6 grid gap-6">
          <Input
            onBlur={formHandler.handleBlur}
            label={
              <>
                Organization Contact <em className="text-sm font-normal">(Optional)</em>
              </>
            }
            errorMessage={
              formHandler?.touched.organizationContact &&
              formHandler?.errors.organizationContact
                ? formHandler?.errors.organizationContact
                : ''
            }
            disabled={formHandler.isSubmitting}
            value={formHandler.values.organizationContact}
            type="tel"
            placeholder="Enter contact number"
            onChange={formHandler.handleChange}
            name="organizationContact"
            otherClasses={{ container: 'w-full' }}
          />
        </div>

        <div className="mt-8 flex flex-col-reverse gap-15 md:grid md:grid-cols-2 md:gap-6 lg:mt-12">
          <Button
            variant="secondary"
            type="button"
            size={sm}
            onClick={() => handleClose()}
            otherClasses="text-brand-posh-pink hover:border-brand-posh-pink hover:text-brand-posh-pink"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            size={sm}
            disable={
              (formHandler?.initialValues?.companyName ===
                formHandler?.values?.companyName &&
                formHandler?.initialValues?.industryType ===
                  formHandler?.values?.industryType &&
                formHandler?.initialValues?.organizationContact ===
                  formHandler?.values?.organizationContact &&
                formHandler?.initialValues?.industrySize ===
                  formHandler?.values?.industrySize &&
                formHandler?.initialValues?.organizationAddress ===
                  formHandler?.values?.organizationAddress ) ||
              formHandler?.isSubmitting
            }
          >
            Update Details
          </Button>
        </div>
      </form>
    </div>
  );
};

export default EditOrganizationDetailsModal;
